import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { 
  Search, X, Loader2, Calendar, Clock, MapPin, 
  ExternalLink, Sparkles, ChevronDown, ChevronUp, Globe 
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Popular tags for suggestions
const POPULAR_TAGS = [
  'Wedding', 'Conference', 'Concert', 'Festival', 'Gala',
  'Birthday', 'Corporate', 'Fundraiser', 'Workshop', 'Meetup',
  'Party', 'Networking', 'Exhibition', 'Launch', 'Celebration'
];

const FindSimilarEvents = ({
  isOpen,
  onToggle,
  selectedCities = [],
  selectedState,
  onEventClick,
  mintStyle = false,
}) => {
  const { getAuthHeaders } = useAuth();
  
  // Search state
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 2 > 12 ? 1 : new Date().getMonth() + 2);
  const [endYear, setEndYear] = useState(new Date().getMonth() + 2 > 12 ? new Date().getFullYear() + 1 : new Date().getFullYear());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  
  // Web search toggle state
  const [includeWebResults, setIncludeWebResults] = useState(true);
  
  // Results state
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchingWeb, setSearchingWeb] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchMessage, setSearchMessage] = useState('');

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() + 1,
    new Date().getFullYear() + 2,
  ];

  const addTag = (tag) => {
    const normalizedTag = tag.trim().toLowerCase();
    if (normalizedTag && !tags.includes(normalizedTag) && tags.length < 5) {
      setTags([...tags, normalizedTag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const handleSearch = async () => {
    if (tags.length === 0) {
      toast.error('Please add at least one tag');
      return;
    }

    setSearching(true);
    setSearchingWeb(false);
    setHasSearched(true);
    setSearchMessage('Searching PlanBlox events...');

    try {
      // Calculate date range
      const startDate = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
      const endDays = new Date(endYear, endMonth, 0).getDate();
      const endDate = `${endYear}-${String(endMonth).padStart(2, '0')}-${endDays}`;

      // Build cities string
      const citiesParam = selectedCities.map(c => c.city).join(',');

      const params = new URLSearchParams({
        tags: tags.join(','),
        start_date: startDate,
        end_date: endDate,
        include_web: includeWebResults ? 'true' : 'false',
      });

      if (citiesParam) params.append('cities', citiesParam);
      if (selectedState) params.append('state', selectedState);
      if (startTime) params.append('start_time', startTime);
      if (endTime) params.append('end_time', endTime);

      const response = await axios.get(
        `${API}/calendar/search-events?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      
      // Update search message based on results
      if (data.webSearchTriggered) {
        setSearchMessage('');
      } else if (data.totalPlanBlox === 0 && !includeWebResults) {
        setSearchMessage('No PlanBlox events found. Enable "Include Web Results" to search the web.');
      } else {
        setSearchMessage('');
      }

      setResults(data.results || []);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
      setResults([]);
      setSearchMessage('');
    } finally {
      setSearching(false);
      setSearchingWeb(false);
    }
  };

  const clearSearch = () => {
    setTags([]);
    setTagInput('');
    setResults([]);
    setHasSearched(false);
    setSearchMessage('');
    setStartTime('');
    setEndTime('');
  };

  // Suggested tags (exclude already selected)
  const suggestedTags = POPULAR_TAGS.filter(t => !tags.includes(t.toLowerCase())).slice(0, 8);

  if (!isOpen) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onToggle}
        className={cn(
          "gap-2",
          mintStyle && "bg-[#7FD1C6] hover:bg-[#6BC4B8] text-black border-[#7FD1C6]"
        )}
        data-testid="find-similar-btn"
      >
        <Search className="h-4 w-4" />
        Find Other Events
      </Button>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-background to-muted/20">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Find Other Events</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="h-8 w-8"
            data-testid="close-find-similar"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tags Input */}
        <div className="space-y-2">
          <Label>Tags (required)</Label>
          <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border rounded-md bg-background">
            {tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="gap-1 pr-1"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="ml-1 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              placeholder={tags.length === 0 ? "Type tags and press Enter..." : "Add more..."}
              className="flex-1 min-w-[120px] border-0 p-0 h-6 focus-visible:ring-0"
              data-testid="tag-input"
            />
          </div>
          {/* Suggested Tags */}
          {suggestedTags.length > 0 && tags.length < 5 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs text-muted-foreground">Suggestions:</span>
              {suggestedTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => addTag(tag)}
                  className="text-xs px-2 py-0.5 bg-muted hover:bg-muted/80 rounded-full transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Location */}
        <div className="space-y-2">
          <Label className="text-muted-foreground text-xs">
            Location: Using your selected cities ({selectedCities.length || 'all'})
          </Label>
        </div>

        {/* Date Range */}
        <div className="space-y-2">
          <Label>Date Range</Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">From</span>
              <div className="flex gap-1">
                <Select value={String(startMonth)} onValueChange={(v) => setStartMonth(Number(v))}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(startYear)} onValueChange={(v) => setStartYear(Number(v))}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-xs text-muted-foreground">To</span>
              <div className="flex gap-1">
                <Select value={String(endMonth)} onValueChange={(v) => setEndMonth(Number(v))}>
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={String(endYear)} onValueChange={(v) => setEndYear(Number(v))}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Time Range (Optional) */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            Time Range
            <span className="text-xs text-muted-foreground">(optional)</span>
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              placeholder="Start time"
              data-testid="start-time"
            />
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              placeholder="End time"
              data-testid="end-time"
            />
          </div>
        </div>

        {/* Include AI Suggestions Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <div>
              <Label htmlFor="include-web" className="cursor-pointer">Include AI Suggestions</Label>
              <p className="text-xs text-muted-foreground">Get AI-generated event ideas when fewer than 7 PlanBlox events are found</p>
            </div>
          </div>
          <Switch
            id="include-web"
            checked={includeWebResults}
            onCheckedChange={setIncludeWebResults}
            data-testid="include-web-toggle"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleSearch}
            disabled={searching || tags.length === 0}
            className="flex-1"
            data-testid="search-btn"
          >
            {searching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {searchingWeb ? 'Searching web...' : 'Searching...'}
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search
              </>
            )}
          </Button>
          {hasSearched && (
            <Button variant="outline" onClick={clearSearch}>
              Clear
            </Button>
          )}
        </div>

        {/* Search Status Message */}
        {searching && (
          <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-primary">{searchMessage || 'Searching...'}</span>
          </div>
        )}

        {/* Results */}
        {hasSearched && !searching && (
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                Results ({results.length})
              </span>
              {results.length === 7 && (
                <span className="text-xs text-muted-foreground">
                  Showing top 7
                </span>
              )}
            </div>

            {results.length === 0 ? (
              <div className="text-center py-6 space-y-2">
                <p className="text-sm text-muted-foreground">
                  No events found matching your criteria
                </p>
                {!includeWebResults && (
                  <p className="text-xs text-muted-foreground">
                    Try enabling &ldquo;Include AI Suggestions&rdquo; to get event ideas
                  </p>
                )}
                {includeWebResults && (
                  <p className="text-xs text-muted-foreground">
                    Try broadening your date range or using different tags
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {results.map((result, idx) => (
                  <button
                    key={result.id || idx}
                    onClick={() => result.source === 'PlanBlox' ? onEventClick(result.id) : window.open(result.url, '_blank')}
                    className={cn(
                      "w-full p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors text-left",
                      result.source === 'PlanBlox' && "hover:border-primary/50"
                    )}
                    data-testid={`search-result-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm truncate">
                            {result.title}
                          </span>
                          <Badge
                            variant={result.source === 'PlanBlox' ? 'default' : 'outline'}
                            className="text-xs shrink-0"
                          >
                            {result.source}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          {result.date && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {result.date}
                            </span>
                          )}
                          {result.time && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {result.time}
                            </span>
                          )}
                          {result.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {result.location}
                            </span>
                          )}
                        </div>
                      </div>
                      {result.source === 'Web' && (
                        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FindSimilarEvents;
